import React, { useCallback, useState } from 'react';
import type { FC } from 'react';
import { useIntl, FormattedMessage } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import { Label } from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import { CloseButton, ModalBody, ModalFooter, useModal } from '@atlaskit/modal-dialog';
import { Box, Text } from '@atlaskit/primitives/compiled';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';

import { ADFRenderer } from '@confluence/adf-renderer/entry-points/ADFRenderer';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets';

import { messages } from '../messages';

type CreateSnippetModalHeaderProps = {
	onClose: () => void;
};

type CreateSnippetModalDialogProps = {
	onCloseComplete: () => void;
} & CreateSnippetModalHeaderProps;

type CreateSnippetModalProps = {
	adf: any;
} & CreateSnippetModalDialogProps;

export enum CreateSnippetModalEvents {
	CANCELLED = 'createSnippetModalCancelled',
	CLOSED = 'createSnippetModalClosed',
	CREATED = 'createSnippetModalCreated',
}

const styles = cssMap({
	headerContainer: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		flexDirection: 'row',
		padding: token('space.300'),
	},
	headerText: {
		display: 'flex',
		alignItems: 'flex-start',
		flexDirection: 'column',
		gap: token('space.050'),
	},
	bodyContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.300'),
		height: '100%',
	},
	contentContainer: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		maxHeight: '340px',
		border: `${token('border.width')} solid ${token('color.border.input')}`,
		borderRadius: token('radius.large'),
		scrollbarColor: `${token('color.border.input')} transparent`,
	},
	adfContainer: {
		overflow: 'auto',
		height: '100%',
		padding: token('space.300'),
	},
});

const CreateSnippetModalHeader = ({ onClose }: CreateSnippetModalHeaderProps) => {
	const { titleId } = useModal();

	return (
		<Box xcss={styles.headerContainer}>
			<Box xcss={styles.headerText}>
				<Heading size="medium" id={titleId}>
					<FormattedMessage {...messages.createTemplateModalTitle} />
				</Heading>
				<Text size="medium" color="color.text.subtle">
					<FormattedMessage {...messages.createTemplateModalDescription} />
				</Text>
			</Box>
			<CloseButton onClick={onClose} />
		</Box>
	);
};

export const CreateSnippetModal: FC<CreateSnippetModalProps> = ({ adf, onClose }) => {
	const intl = useIntl();
	const [snippetName, setSnippetName] = useState('');
	const [adfError, setADFError] = useState<any>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { Modal } = useChoreographerAssets();

	const fireCreateSnippetModalAnalytics = useCallback(
		(event: CreateSnippetModalEvents) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'confluenceCreateSnippetModal',
					action: 'closed',
					actionSubject: 'button',
					actionSubjectId: 'createSnippet',
					type: 'ui',
					attributes: {
						event,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const exitModal = useCallback(
		(event: CreateSnippetModalEvents) => {
			fireCreateSnippetModalAnalytics(event);
			onClose();
		},
		[fireCreateSnippetModalAnalytics, onClose],
	);

	const createSnippet = useCallback(() => {
		fireCreateSnippetModalAnalytics(CreateSnippetModalEvents.CREATED);
		onClose();
	}, [fireCreateSnippetModalAnalytics, onClose]);

	const handleADFError = useCallback(
		(error: any) => {
			setADFError(error);
		},
		[setADFError],
	);

	return (
		<Modal height="600px" testId="modal-dialog-create-snippet">
			<CreateSnippetModalHeader onClose={() => exitModal(CreateSnippetModalEvents.CLOSED)} />

			<ModalBody>
				<Box xcss={styles.bodyContainer} testId="modal-dialog-create-snippet">
					<Box testId="modal-dialog-create-snippet-name">
						<Label htmlFor="snippet-name">
							<FormattedMessage {...messages.createTemplateModalNameLabel} />
						</Label>
						{/* TODO: Add character limit validation */}
						<Textfield
							id="snippet-name"
							testId="modal-dialog-create-snippet-name-input"
							placeholder={intl.formatMessage(messages.createTemplateModalNamePlaceholder)}
							value={snippetName}
							onChange={(e: any) => setSnippetName(e.currentTarget.value || '')}
						/>
					</Box>
					<Box xcss={styles.contentContainer} testId="modal-dialog-create-snippet-content">
						<Box xcss={styles.adfContainer}>
							<ADFRenderer
								document={adf}
								appearance="comment"
								allowPlaceholderText
								onError={handleADFError}
							/>
						</Box>
					</Box>
				</Box>
			</ModalBody>

			<ModalFooter>
				<Button
					appearance="subtle"
					testId="modal-dialog-create-snippet-cancel-button"
					onClick={() => exitModal(CreateSnippetModalEvents.CANCELLED)}
				>
					<FormattedMessage {...messages.createTemplateModalCancelButton} />
				</Button>
				<Button
					appearance="primary"
					testId="modal-dialog-create-snippet-create-button"
					onClick={() => createSnippet()}
					isDisabled={!snippetName || !!adfError}
				>
					<FormattedMessage {...messages.createTemplateModalCreateButton} />
				</Button>
			</ModalFooter>
		</Modal>
	);
};
